import * as React from "react";
import uuid from "uuid";
// hooks
import { useActions } from "app/store";
import { useCancelTokenWithCancelHandler } from "common/hooks/useCancelToken";
import useRedirect from "common/hooks/useRedirect";
import useIsMobile from "common/hooks/useIsMobile";
// actions
import { fetchDQuestGroupQuests as fetchDQuestGroupQuestsAction } from "app/actions/dquest";
// helpers
import { MoimURL } from "common/helpers/url";
// component
import { ArrowContainer } from "common/components/blockitEditorBase/styled";

import DquestGroupItemList from "./listComponent";
import { withPlacement } from "../../hoc/withPlacement";
import InViewTrigger from "../../components/inViewTrigger";
import MoreButton from "../../components/moreButton";
import { useIntlShort } from "common/hooks/useIntlShort";
import { Spacer } from "common/components/designSystem/spacer";
import { useParseListElementConfig } from "common/components/blockitListLayout/hooks/useParseListElementConfig";

const DEFAULT_LIMIT = 20;

const DQuestGroupPreview: React.FC<Moim.Blockit.V2.IDQuestGroupListPreviewBlock> = ({
  listElement,
  itemStyle,
  itemStyle_web,
  resourceId,
  moreButton,
}) => {
  const intl = useIntlShort();
  const isMobile = useIsMobile();
  const redirect = useRedirect();
  const [quests, setQuests] = React.useState<Moim.Id[] | undefined>(undefined);

  const { cancelTokenSource, handleCancel } = useCancelTokenWithCancelHandler();
  const [isLoading, setLoadStatus] = React.useState<boolean | undefined>(
    undefined,
  );
  const portalSectionId = React.useMemo(
    () => `dquest-group-preview-portal-${uuid.v4()}`,
    [],
  );
  const { fetchDQuestGroupQuests } = useActions({
    fetchDQuestGroupQuests: fetchDQuestGroupQuestsAction,
  });

  const itemStyleConfig =
    !isMobile && Boolean(itemStyle_web) ? itemStyle_web : itemStyle;

  const { listElementType, maxVisibleCount } = useParseListElementConfig(
    listElement,
  );

  const handleClickViewMore = React.useCallback(() => {
    redirect(
      new MoimURL.QuestGroupQuests({
        id: resourceId,
      }).toString(),
    );
  }, [redirect, resourceId]);

  const handleOnView = React.useCallback(() => {
    if (!isLoading) {
      setLoadStatus(true);
      fetchDQuestGroupQuests(
        resourceId,
        {
          limit: maxVisibleCount ?? DEFAULT_LIMIT,
        },
        cancelTokenSource.current.token,
      )
        .then(response => {
          setQuests(response.data);
        })
        .finally(() => {
          setLoadStatus(false);
        });
    }
  }, [
    isLoading,
    fetchDQuestGroupQuests,
    resourceId,
    cancelTokenSource,
    maxVisibleCount,
  ]);

  React.useLayoutEffect(
    () => () => {
      handleCancel();
    },
    [handleCancel],
  );

  return (
    <>
      <InViewTrigger onVisible={handleOnView} />

      {listElementType === "horizontal" && (
        <ArrowContainer id={portalSectionId} />
      )}
      <DquestGroupItemList
        quests={quests}
        portalSectionId={portalSectionId}
        isLoading={Boolean(
          (!quests?.length && isLoading === undefined) || isLoading,
        )}
        listElement={listElement}
        itemStyleConfig={itemStyleConfig}
      />
      {moreButton?.show ? (
        <>
          <Spacer value={24} />
          <MoreButton
            text={intl("button_see_more_quests")}
            onClick={handleClickViewMore}
          />
        </>
      ) : null}
    </>
  );
};

export default withPlacement(React.memo(DQuestGroupPreview));
